import { apiFetch } from './client';

export const ORDER_STATUS = {
  RECEIVED: 'RECEIVED',
  CONFIRMED: 'CONFIRMED',
  PACKED: 'PACKED',
  OUT_FOR_DELIVERY: 'OUT_FOR_DELIVERY',
  DELIVERED: 'DELIVERED',
  CANCELLED: 'CANCELLED',
  FAILED_DELIVERY: 'FAILED_DELIVERY',
};

export const ORDER_QUEUE_COLUMNS = [
  { key: ORDER_STATUS.RECEIVED, title: 'Received', hint: 'New orders to confirm' },
  { key: ORDER_STATUS.CONFIRMED, title: 'Confirmed', hint: 'Ready to pack' },
  { key: ORDER_STATUS.PACKED, title: 'Packed', hint: 'Ready for a rider' },
  { key: ORDER_STATUS.OUT_FOR_DELIVERY, title: 'Out for delivery', hint: 'On the road' },
  { key: ORDER_STATUS.DELIVERED, title: 'Delivered', hint: 'Completed' },
  { key: ORDER_STATUS.FAILED_DELIVERY, title: 'Failed', hint: 'Needs attention' },
];

const QUEUE_COLUMN_KEYS = new Set(ORDER_QUEUE_COLUMNS.map((col) => col.key));

/**
 * Columns a debounced batch of live events can refresh.
 * Returns null when any event does not name a queue column, so the caller reloads the board.
 */
export function queueColumnsForEvents(events) {
  if (!Array.isArray(events) || events.length === 0) return null;
  const keys = new Set();
  for (const event of events) {
    const next = String(event?.status || '').toUpperCase();
    const prev = String(event?.previous_status || event?.previousStatus || '').toUpperCase();
    if (!next && !prev) return null;
    let known = false;
    if (QUEUE_COLUMN_KEYS.has(prev)) {
      keys.add(prev);
      known = true;
    }
    if (QUEUE_COLUMN_KEYS.has(next)) {
      keys.add(next);
      known = true;
    }
    if (!known) return null;
  }
  return keys.size ? [...keys] : null;
}

export const PAYMENT_METHOD_LABELS = {
  COD: 'Cash on Delivery',
  CASH: 'Cash',
  UPI: 'UPI',
  CARD: 'Card',
  ONLINE: 'Online',
};

export const ORDER_QUEUE_ERRORS = {
  invalid_transition: 'This order has already moved on — refresh to see its latest status.',
  cannot_cancel: 'This order can no longer be cancelled.',
  cannot_unassign_in_transit: 'Unassign the rider before the order is out for delivery.',
  delivery_person_sub_required: 'Pick a rider before assigning.',
  invalid_status: 'This order is not in a status that allows this action.',
  not_found: 'This order could not be found — it may have been removed.',
  forbidden: 'You do not have permission to do this.',
  payment_required: 'This online order is not paid yet. It can be confirmed after payment is captured.',
  order_id_or_phone_required: 'Enter an order ID or phone number to search.',
  cognito_error: 'Could not look up that phone number right now — try again.',
};

function pickFirstString(...candidates) {
  for (const value of candidates) {
    if (typeof value === 'string' && value.trim()) return value.trim();
  }
  return null;
}

/** Cognito usernames for customers are `p` + digits, e.g. p919876543210. */
function phoneFromUsername(username) {
  if (!username || typeof username !== 'string') return null;
  const match = username.trim().match(/^p(\d{10,15})$/i);
  return match ? `+${match[1]}` : null;
}

export const DELIVERY_MODE = {
  INSTANT: 'INSTANT',
  SCHEDULED: 'SCHEDULED',
};

/** Formats an ISO slot window as "5:00 PM – 6:00 PM"; falls back to just the start time. */
export function formatSlotWindow(slotStart, slotEnd) {
  if (!slotStart) return null;
  const start = new Date(slotStart);
  if (Number.isNaN(start.getTime())) return null;
  const startLabel = start.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' });
  if (!slotEnd) return startLabel;
  const end = new Date(slotEnd);
  if (Number.isNaN(end.getTime())) return startLabel;
  return `${startLabel} – ${end.toLocaleTimeString('en-IN', { hour: 'numeric', minute: '2-digit' })}`;
}

/**
 * Ops/Driver queue priority: an instant order is "due" the moment it was placed,
 * a scheduled order is due at its slot start — whichever is sooner should surface first.
 * Due times are computed once per order, then compared as numbers.
 */
export function orderDueTime(order) {
  if (order.deliveryMode === DELIVERY_MODE.SCHEDULED && order.slotStart) {
    const slot = new Date(order.slotStart).getTime();
    if (!Number.isNaN(slot)) return slot;
  }
  if (!order.placedAt) return Infinity;
  const placed = new Date(order.placedAt).getTime();
  return Number.isNaN(placed) ? Infinity : placed;
}

export function sortByUrgency(orders) {
  return orders
    .map((order) => ({ order, due: orderDueTime(order) }))
    .sort((a, b) => a.due - b.due)
    .map((entry) => entry.order);
}

export function normalizeOrder(raw) {
  const envelope = raw && typeof raw === 'object' ? raw : {};
  const order = envelope.order || envelope;
  const addr = order.address_snapshot || envelope.address_snapshot || {};
  const customer = order.customer || envelope.customer || {};
  const contact = addr.contact || order.contact || envelope.contact || {};
  const lineItems = order.line_items || [];
  const slot = order.slot || order.scheduled_slot || order.delivery_slot || envelope.slot || {};

  const slotId = pickFirstString(order.slot_id, envelope.slot_id, slot.id, slot.slot_id);
  const slotStart = pickFirstString(
    order.slot_start, order.slot_starts_at, order.slot_start_time,
    slot.start_time, slot.starts_at, slot.start,
  );
  const slotEnd = pickFirstString(
    order.slot_end, order.slot_ends_at, order.slot_end_time,
    slot.end_time, slot.ends_at, slot.end,
  );
  const deliveryMode = pickFirstString(order.delivery_mode, envelope.delivery_mode)
    || (slotId || slotStart ? DELIVERY_MODE.SCHEDULED : DELIVERY_MODE.INSTANT);

  const phone = pickFirstString(
    envelope.customer_phone,
    envelope.customer_phone_number,
    envelope.phone,
    envelope.phone_number,
    order.customer_phone,
    order.customer_phone_number,
    order.phone_number,
    order.phone,
    customer.phone_number,
    customer.phone,
    addr.phone_number,
    addr.phone,
    addr.mobile,
    addr.contact_phone,
    contact.phone,
    contact.phone_number,
    contact.mobile,
    phoneFromUsername(
      order.customer_username
      || customer.username
      || envelope.customer_username
      || order.username
      || customer.Username,
    ),
  );

  const name = pickFirstString(
    envelope.customer_name,
    order.customer_name,
    customer.name,
    addr.name,
    addr.recipient_name,
    addr.contact_name,
    contact.name,
  );

  return {
    id: order.order_id || envelope.order_id || '',
    status: order.status || envelope.status || 'RECEIVED',
    channel: order.channel || envelope.channel || 'DELIVERY',
    subscriptionId: order.subscription_id || envelope.subscription_id || null,
    dropId: order.drop_id || envelope.drop_id || null,
    dropDate: order.drop_date || envelope.drop_date || null,
    customer: {
      sub: order.customer_sub || customer.sub || envelope.customer_sub || null,
      name,
      phone,
    },
    address: addr.line1 ? [addr.line1, addr.line2, addr.area, addr.city].filter(Boolean).join(', ') : null,
    items: lineItems.map((it) => ({
      productId: it.item_id,
      name: it.name,
      price: Number(it.unit_price ?? 0),
      qty: Number(it.qty ?? 0),
      unit: it.unit || null,
      soldBy: String(it.sold_by || '').toUpperCase() === 'WEIGHT' ? 'WEIGHT' : 'EACH',
      returnStatus: it.return_status || null,
    })),
    subtotal: Number(order.subtotal ?? envelope.subtotal ?? 0),
    discountAmount: Number(order.discount_amount ?? 0),
    deliveryFee: Number(order.delivery_fee ?? 0),
    total: Number(order.total ?? envelope.total ?? 0),
    storeCreditUsed: Number(order.store_credit_used ?? envelope.store_credit_used ?? 0),
    amountPaid: Number(order.amount_paid ?? envelope.amount_paid ?? (Number(order.total ?? 0) - Number(order.store_credit_used ?? 0))),
    couponCode: order.coupon_code || null,
    paymentMethod: order.payment_method || 'COD',
    paymentStatus: order.payment_status || null,
    placedAt: order.created_at || null,
    updatedAt: order.updated_at || null,
    deliveryMode,
    slotId,
    slotStart,
    slotEnd,
    deliveryPersonSub: order.delivery_person_sub || null,
    rider: null,
    notes: order.notes || order.delivery_notes || '',
    failureReason: order.fail_reason || order.cancel_reason || null,
  };
}

function riderAttributes(raw) {
  if (raw?.attributes && !Array.isArray(raw.attributes) && typeof raw.attributes === 'object') {
    return raw.attributes;
  }
  const out = {};
  (raw?.Attributes || raw?.UserAttributes || []).forEach((entry) => {
    if (entry?.Name) out[entry.Name] = entry.Value;
  });
  return out;
}

function looksLikePhone(value) {
  const text = String(value || '').trim();
  if (!text || /[a-z]/i.test(text)) return false;
  const digits = text.replace(/\D/g, '');
  return digits.length >= 10 && digits.length <= 15;
}

function nameFromEmail(email) {
  if (!email || !email.includes('@')) return '';
  const local = email.split('@')[0].replace(/[._-]+/g, ' ').trim();
  if (!local || looksLikePhone(local)) return '';
  return local.replace(/\b\w/g, (letter) => letter.toUpperCase());
}

/** Display name is never the phone number. Phone stays on its own field. */
export function normalizeRider(raw) {
  const attrs = riderAttributes(raw || {});
  const phone = raw?.phone_number || raw?.phone || attrs.phone_number || null;
  const given = [raw?.given_name || attrs.given_name, raw?.family_name || attrs.family_name]
    .filter(Boolean)
    .join(' ')
    .trim();
  const candidates = [
    given,
    raw?.full_name,
    raw?.fullName,
    raw?.display_name,
    raw?.rider_name,
    attrs.name,
    attrs['custom:name'],
    raw?.name,
    nameFromEmail(raw?.email || attrs.email),
  ];
  const name = candidates
    .map((value) => String(value || '').trim())
    .find((value) => value && !looksLikePhone(value)) || 'Rider';
  return {
    id: raw?.sub || raw?.username || attrs.sub || '',
    phone,
    email: raw?.email || attrs.email || null,
    name,
    status: raw?.status || null,
    enabled: raw?.enabled !== false,
  };
}

export async function listOrders(status, token) {
  const raw = await apiFetch(`/store/orders?status=${encodeURIComponent(status)}`, { token });
  return (raw?.orders || []).map(normalizeOrder);
}

export async function getOrdersSummary(token) {
  const raw = await apiFetch('/store/orders/summary', { token });
  const counts = raw?.counts || {};
  return {
    counts,
    queueBadge: raw?.queue_badge != null ? Number(raw.queue_badge) || 0 : null,
    awaitingConfirmation: raw?.awaiting_confirmation ?? 0,
    awaitingPayment: raw?.awaiting_payment ?? 0,
    outForDelivery: raw?.out_for_delivery ?? 0,
    delivered: raw?.delivered ?? 0,
  };
}

export async function listRiders(token) {
  const raw = await apiFetch('/store/riders', { token });
  return (raw?.riders || []).filter((r) => r.enabled !== false).map(normalizeRider);
}

export async function confirmOrder(orderId, token) {
  const raw = await apiFetch(`/store/orders/${encodeURIComponent(orderId)}/confirm`, { method: 'POST', token });
  return normalizeOrder(raw);
}

export async function packOrder(orderId, token) {
  const raw = await apiFetch(`/store/orders/${encodeURIComponent(orderId)}/pack`, { method: 'POST', token });
  return normalizeOrder(raw);
}

export async function assignRider(orderId, deliveryPersonSub, token) {
  const raw = await apiFetch(`/store/orders/${encodeURIComponent(orderId)}/assign`, {
    method: 'POST',
    token,
    body: { delivery_person_sub: deliveryPersonSub },
  });
  return normalizeOrder(raw);
}

export async function unassignRider(orderId, token) {
  const raw = await apiFetch(`/store/orders/${encodeURIComponent(orderId)}/unassign`, { method: 'POST', token });
  return normalizeOrder(raw);
}

export async function cancelOrder(orderId, reason, token) {
  const raw = await apiFetch(`/store/orders/${encodeURIComponent(orderId)}/cancel`, {
    method: 'POST',
    token,
    body: reason ? { reason } : undefined,
  });
  return normalizeOrder(raw);
}

export async function resendBill(orderId, token) {
  const raw = await apiFetch(`/store/orders/${encodeURIComponent(orderId)}/bill/resend`, { method: 'POST', token });
  return { ok: !!raw?.ok, emailSent: !!raw?.email_sent };
}

export async function lookupOrders({ orderId, phone } = {}, token) {
  const params = new URLSearchParams();
  if (orderId) params.set('order_id', orderId);
  else if (phone) params.set('phone', phone);
  const raw = await apiFetch(`/store/orders/lookup?${params.toString()}`, { token });
  const orders = (raw?.orders || []).map(normalizeOrder);
  if (raw?.phone) {
    orders.forEach((o) => { o.customer.phone = raw.phone; });
  }
  return {
    phone: raw?.phone || null,
    customerSub: raw?.customer_sub || null,
    orders,
  };
}

export async function getOrderBill(orderId, token) {
  const raw = await apiFetch(`/orders/${encodeURIComponent(orderId)}/bill`, { token });
  return {
    invoice: raw?.invoice || null,
    billS3Key: raw?.bill_s3_key || null,
    billPdfS3Key: raw?.bill_pdf_s3_key || null,
    presignedUrl: raw?.presigned_url || null,
    pdfPresignedUrl: raw?.pdf_presigned_url || null,
  };
}
